import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, User, Mail, Phone, MapPin, Car, Plus, Trash2, Briefcase,
  Receipt, MessageCircle,
} from "lucide-react";

export default function MasterCustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [comms, setComms] = useState(null);
  const [veh, setVeh] = useState({ vin: "", year: "", make: "", model: "", license_plate: "" });

  const load = () => api.get(`/master/customers/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); api.get(`/master/customers/${id}/communications`).then((r) => setComms(r.data)).catch(() => {}); }, [id]);

  const addVehicle = async () => {
    if (!veh.vin && !veh.make) return toast.error("VIN or make required");
    await api.post(`/master/customers/${id}/vehicles`, { ...veh, year: parseInt(veh.year) || null });
    toast.success("Vehicle added");
    setVeh({ vin: "", year: "", make: "", model: "", license_plate: "" });
    load();
  };

  const removeVehicle = async (vid) => {
    await api.delete(`/master/customers/${id}/vehicles/${vid}`);
    load();
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const c = data.customer;

  return (
    <div className="space-y-6 animate-slide-up" data-testid="customer-detail-page">
      <Link to="/master/customers" className="text-xs text-slate-500 hover:text-blue-400 flex items-center gap-1 w-fit"><ArrowLeft className="w-3 h-3" /> Back to Customers</Link>

      <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-[#0B1120] to-[#0A0F1C] p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-blue-600/15 border border-blue-500/20 flex items-center justify-center">
            <User className="w-7 h-7 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-3xl font-semibold tracking-tight">{c.first_name} {c.last_name}</h1>
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-500">
              {c.email && <span className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{c.email}</span>}
              {c.phone && <span className="flex items-center gap-1.5 font-mono"><Phone className="w-3 h-3" />{c.phone}</span>}
              {(c.city || c.state) && <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{[c.address, c.city, c.state, c.zip_code].filter(Boolean).join(", ")}</span>}
            </div>
            <div className="flex gap-2 mt-2">
              {c.insurance_company && <Badge className="bg-cyan-500/15 text-cyan-300 border-cyan-500/20">{c.insurance_company}</Badge>}
              {c.policy_number && <Badge className="bg-slate-800 border-slate-700 text-slate-300 font-mono">{c.policy_number}</Badge>}
              {c.sms_opt_in && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/20">SMS OK</Badge>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3 flex items-center gap-1.5"><Car className="w-3 h-3" />Vehicles · {(c.vehicles || []).length}</div>
          <div className="space-y-2 mb-3">
            {(c.vehicles || []).map((v) => (
              <div key={v.id} data-testid={`veh-${v.id}`} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 flex items-center gap-3">
                <Car className="w-4 h-4 text-blue-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{v.year} {v.make} {v.model}</div>
                  <div className="text-[10px] font-mono text-slate-500">{v.vin || "no-vin"} {v.license_plate && `· ${v.license_plate}`}</div>
                </div>
                <button data-testid={`veh-remove-${v.id}`} onClick={() => removeVehicle(v.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            {(c.vehicles || []).length === 0 && <div className="text-center text-slate-500 text-xs py-3">No vehicles yet</div>}
          </div>
          <div className="border-t border-slate-800 pt-3 grid grid-cols-5 gap-2">
            <Input data-testid="veh-vin" placeholder="VIN" value={veh.vin} onChange={(e) => setVeh({ ...veh, vin: e.target.value.toUpperCase() })} className="bg-[#030712] border-slate-800 col-span-2 font-mono text-xs" />
            <Input data-testid="veh-year" placeholder="Year" type="number" value={veh.year} onChange={(e) => setVeh({ ...veh, year: e.target.value })} className="bg-[#030712] border-slate-800" />
            <Input data-testid="veh-make" placeholder="Make" value={veh.make} onChange={(e) => setVeh({ ...veh, make: e.target.value })} className="bg-[#030712] border-slate-800" />
            <Input data-testid="veh-model" placeholder="Model" value={veh.model} onChange={(e) => setVeh({ ...veh, model: e.target.value })} className="bg-[#030712] border-slate-800" />
          </div>
          <Button data-testid="veh-add" onClick={addVehicle} className="mt-2 w-full bg-blue-600 hover:bg-blue-500"><Plus className="w-3 h-3 mr-1.5" />Add Vehicle</Button>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3 flex items-center gap-1.5"><Briefcase className="w-3 h-3" />Jobs · {data.jobs.length}</div>
          {data.jobs.length === 0 ? (
            <div className="text-center text-slate-500 py-6 text-sm">No jobs</div>
          ) : (
            <div className="space-y-1.5">
              {data.jobs.slice(0, 6).map((j) => (
                <Link key={j.id} to={`/jobs/${j.id}`} className="block rounded-lg bg-slate-900/40 border border-slate-800 px-3 py-2 hover:border-blue-500/40 transition-colors">
                  <div className="text-xs font-mono text-blue-300 truncate">{j.vin}</div>
                  <div className="flex items-center justify-between mt-1">
                    <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px]">{j.status}</Badge>
                    <div className="text-[10px] text-slate-500">${(j.total_amount || 0).toLocaleString()}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3 flex items-center gap-1.5"><Receipt className="w-3 h-3" />Invoices · {data.invoices.length}</div>
          {data.invoices.length === 0 ? (
            <div className="text-center text-slate-500 py-6 text-sm">No invoices</div>
          ) : (
            <div className="divide-y divide-slate-800/60">
              {data.invoices.slice(0, 8).map((i) => (
                <div key={i.id} className="flex items-center gap-3 py-2 text-xs">
                  <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px]">{i.type}</Badge>
                  <Badge className={`text-[10px] ${i.status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/20" : "bg-amber-500/15 text-amber-300 border-amber-500/20"}`}>{i.status}</Badge>
                  <div className="flex-1 font-mono text-slate-500 truncate">{i.id.slice(0, 8)}</div>
                  <div className="font-mono text-slate-300">${(i.total || 0).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3 flex items-center gap-1.5"><MessageCircle className="w-3 h-3" />Communications</div>
          {!comms || (comms.emails.length === 0 && comms.sms.length === 0) ? (
            <div className="text-center text-slate-500 py-6 text-sm">No emails or SMS sent yet</div>
          ) : (
            <div className="space-y-1.5">
              {comms.emails.slice(0, 5).map((e, idx) => (
                <div key={idx} className="rounded-lg bg-slate-900/40 border border-slate-800 px-3 py-2">
                  <div className="text-xs text-slate-300 truncate">📧 {e.subject || "(no subject)"}</div>
                  <div className="text-[10px] font-mono text-slate-500">{new Date(e.created_at).toLocaleString()}</div>
                </div>
              ))}
              {comms.sms.slice(0, 5).map((s, idx) => (
                <div key={`sms-${idx}`} className="rounded-lg bg-slate-900/40 border border-slate-800 px-3 py-2">
                  <div className="text-xs text-slate-300 truncate">📱 {s.body}</div>
                  <div className="text-[10px] font-mono text-slate-500">{new Date(s.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
